import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { brl } from '../../utils/format';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const FORMA_PAGAMENTO_LABEL = { dinheiro: 'Dinheiro', pix: 'Pix', cartao: 'Cartão' };

const mesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const mesLabel = (mes) => {
  const [y, m] = mes.split('-');
  return `${MESES[Number(m) - 1]} de ${y}`;
};

const addMonth = (mes, delta) => {
  let [y, m] = mes.split('-').map(Number);
  m += delta;
  if (m < 1) { m = 12; y -= 1; }
  if (m > 12) { m = 1; y += 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
};

export default function FluxoCaixa() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mes, setMes] = useState(mesAtual());

  useEffect(() => { load(); }, [mes]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/financeiro/vendas', { params: { mes } });
      setItems(res.data);
    } catch { toast.error('Erro ao carregar fluxo de caixa'); }
    finally { setLoading(false); }
  };

  const total = items.reduce((s, i) => s + i.valor_total, 0);

  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-white">Fluxo de Caixa</h1>
        <p className="text-xs text-zinc-500 mt-0.5">Vendas registradas via fechamento de pagamento da Agenda</p>
      </div>

      {/* Navegação de mês */}
      <div className="flex items-center justify-center gap-4">
        <button onClick={() => setMes(m => addMonth(m, -1))} className="p-2 text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors"><ChevronLeft size={16} /></button>
        <span className="text-sm font-medium text-white w-40 text-center">{mesLabel(mes)}</span>
        <button onClick={() => setMes(m => addMonth(m, 1))} className="p-2 text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors"><ChevronRight size={16} /></button>
      </div>

      <div className="stat-card w-fit mx-auto px-8">
        <div className="text-xs text-zinc-500 mb-1 text-center">Total do mês</div>
        <div className="font-mono font-bold text-lg text-white text-center">{brl(total)}</div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-800">
            <tr className="text-xs text-zinc-500">
              <th className="text-left px-4 py-3">Data</th>
              <th className="text-left px-4 py-3">Cliente</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Itens</th>
              <th className="text-left px-4 py-3">Forma de pagamento</th>
              <th className="text-right px-4 py-3">Valor</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {loading ? (
              <tr><td colSpan={5} className="text-center py-12"><div className="inline-block w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></td></tr>
            ) : items.map(item => (
              <tr key={item.id} className="table-row-hover">
                <td className="px-4 py-3 text-zinc-400">{item.data.split('-').reverse().join('/')}</td>
                <td className="px-4 py-3 text-white">{item.client_nome}</td>
                <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{item.itens.map(i => i.descricao).join(', ')}</td>
                <td className="px-4 py-3">
                  <span className="badge bg-orange-500/20 text-orange-400">
                    {FORMA_PAGAMENTO_LABEL[item.forma_pagamento] || item.forma_pagamento}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono text-white">{brl(item.valor_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && items.length === 0 && (
          <div className="text-center py-12 text-zinc-500 text-sm">Nenhuma venda registrada neste mês</div>
        )}
      </div>
    </div>
  );
}
